// Renderiza el arbol de destinos y la lista de hoteles para Fastbooking Lite.
const createRenderService = (host) => {
  const service = {
    getHotelNodeId(hotel, parentId, index) {
      const fallbackId = `${parentId || "root"}-${hotel?.hotelName || "hotel"}-${index}`;
      return `hotel:${hotel?.hotelId || hotel?.pagePath || fallbackId}`;
    },

    getRootListFromBlock(block) {
      if (!block) {
        return null;
      }

      const destinationTree = block.querySelector(".fb-lite-destinationTree-JS");
      if (destinationTree) {
        return destinationTree;
      }

      return Array.from(block.children).find((child) => child.tagName === "UL") || null;
    },

    getLevelClassByDepth(depth) {
      if (depth <= 1) {
        return host.props.modifiers.levels.first;
      }

      if (depth === 2) {
        return host.props.modifiers.levels.second;
      }

      if (depth === 3) {
        return host.props.modifiers.levels.third;
      }

      return host.props.modifiers.levels.fourth;
    },

    countHotelsInSubtree(destinationNode) {
      if (!destinationNode) {
        return 0;
      }

      const ownHotels = Array.isArray(destinationNode.hotels) ? destinationNode.hotels.length : 0;
      const children = Array.isArray(destinationNode.subDestinations) ? destinationNode.subDestinations : [];
      const childrenHotels = children.reduce((total, child) => total + service.countHotelsInSubtree(child), 0);

      return ownHotels + childrenHotels;
    },

    createArrowIconElement(arrowTemplateHtml) {
      if (!arrowTemplateHtml) {
        return null;
      }

      const wrapper = document.createElement("div");
      wrapper.innerHTML = arrowTemplateHtml;
      return wrapper.firstElementChild;
    },

    createDestinationAccordion({ destinationTitle, hotelCount, normalizedHotelsLabel, arrowTemplateHtml = "", includeArrow = false }) {
      const accordion = document.createElement("div");
      accordion.className = "c-fastbooking-lite__form__modal__accordion mod--hover-grey fb-lite-modalContentTitle-JS";

      const accordionText = document.createElement("div");
      accordionText.className = "c-fastbooking-lite__form__modal__accordionText";

      const text = document.createElement("span");
      text.className = "c-fastbooking-lite__form__modal__accordionText__text fb-lite-modalText-JS";
      text.dataset.text = destinationTitle;
      text.textContent = destinationTitle;

      const quantity = document.createElement("span");
      quantity.className = "c-fastbooking-lite__form__modal__hotelQuantity";
      quantity.textContent = `- ${hotelCount} ${normalizedHotelsLabel}`;

      accordionText.append(text, quantity);
      accordion.append(accordionText);

      if (includeArrow) {
        const arrowElement = service.createArrowIconElement(arrowTemplateHtml);
        if (arrowElement) {
          accordion.append(arrowElement);
        }
      }

      return { accordion, text, quantity };
    },

    renderDestinationTreeNode(destinationNode, depth, arrowTemplateHtml, hotelsLabel) {
      const levelClass = service.getLevelClassByDepth(depth);
      const childLevelClass = service.getLevelClassByDepth(depth + 1);
      const hasChildren = Array.isArray(destinationNode?.subDestinations) && destinationNode.subDestinations.length > 0;
      const hotelCount = service.countHotelsInSubtree(destinationNode);
      const hasPath = Boolean(destinationNode?.path);
      const normalizedHotelsLabel = String(hotelsLabel || "hoteles").trim() || "hoteles";
      const destinationTitle = destinationNode?.i18nTitle || "";

      const item = document.createElement("li");
      item.setAttribute("role", "treeitem");
      item.setAttribute("tabindex", "0");
      item.dataset.destinationId = destinationNode?.id || "";
      item.dataset.destinationPath = destinationNode?.path || "";
      item.className = `c-fastbooking-lite__form__modal__content__item ${levelClass} fb-lite-modalContentItem-JS`;

      if (!hasPath) {
        item.classList.add("mod--without-path", host.props.modifiers.structuralNode);
      }

      if (hasChildren) {
        const { accordion } = service.createDestinationAccordion({
          destinationTitle,
          hotelCount,
          normalizedHotelsLabel,
          arrowTemplateHtml,
          includeArrow: true,
        });

        item.append(accordion);

        const childList = document.createElement("ul");
        childList.setAttribute("role", "group");
        childList.className = `c-fastbooking-lite__form__modal__content ${childLevelClass}`;

        destinationNode.subDestinations.forEach((childNode) => {
          childList.append(service.renderDestinationTreeNode(childNode, depth + 1, arrowTemplateHtml, normalizedHotelsLabel));
        });

        item.append(childList);
        return item;
      }

      const { accordion, text, quantity } = service.createDestinationAccordion({
        destinationTitle,
        hotelCount,
        normalizedHotelsLabel,
      });

      if (depth <= 2) {
        item.append(accordion);
        return item;
      }

      item.classList.add("mod--hover-grey");
      item.append(text, quantity);

      return item;
    },

    flattenHotelsForRender(treeData) {
      const hotels = [];

      const walk = (node) => {
        if (!node?.id) {
          return;
        }

        const ownHotels = Array.isArray(node.hotels) ? node.hotels : [];
        ownHotels.forEach((hotel, index) => {
          hotels.push({
            ...hotel,
            _nodeId: service.getHotelNodeId(hotel, node.id, index),
            _destinationId: node.id,
          });
        });

        (Array.isArray(node.subDestinations) ? node.subDestinations : []).forEach((childNode) => walk(childNode));
      };

      (Array.isArray(treeData) ? treeData : []).forEach((rootNode) => walk(rootNode));
      return hotels;
    },

    sortHotelsByRanking(hotels) {
      return (Array.isArray(hotels) ? hotels : [])
        .map((hotel, index) => {
          const parsedRanking = Number.parseInt(String(hotel?.ranking || "").trim(), 10);
          return {
            hotel,
            index,
            ranking: Number.isFinite(parsedRanking) ? parsedRanking : Number.POSITIVE_INFINITY,
          };
        })
        .sort((left, right) => {
          if (left.ranking !== right.ranking) {
            return left.ranking - right.ranking;
          }

          return left.index - right.index;
        })
        .map((entry) => entry.hotel);
    },

    createHotelListItem(hotel, extraClassName = "") {
      const classes = ["c-fastbooking-lite__form__modal__content__item", "mod--hover-blue", "fb-lite-modalContentItem-JS", extraClassName].filter(Boolean).join(" ");

      const item = document.createElement("li");
      item.setAttribute("role", "button");
      item.setAttribute("tabindex", "0");
      item.className = classes;
      item.dataset.text = hotel?.hotelName || "";
      item.dataset.hotelId = hotel?._nodeId || "";
      if (hotel?.meetingPath) {
        item.dataset.meetingPath = hotel.meetingPath;
      }
      if (hotel?.pagePath) {
        item.dataset.pagePath = hotel.pagePath;
      }
      if (Array.isArray(hotel?.eventTypes) && hotel.eventTypes.length) {
        item.dataset.eventTypes = hotel.eventTypes
          .map((eventType) => String(eventType || "").trim())
          .filter(Boolean)
          .join(",");
      }
      item.textContent = hotel?.hotelName || "";

      return item;
    },

    normalizeResortAlias(value) {
      return String(value || "").trim();
    },

    buildResortParentNodeId(resortName) {
      return `hotel:${service.normalizeResortAlias(resortName).toLowerCase().replaceAll(/\s+/g, "-")}`;
    },

    groupHotelsByResort(hotels = []) {
      const resortGroups = new Map();

      hotels.forEach((hotel) => {
        const resortName = service.normalizeResortAlias(hotel?.resortAlias);
        if (!resortName) {
          resortGroups.set(hotel._nodeId, { type: "single", hotels: [hotel] });
          return;
        }

        if (!resortGroups.has(resortName)) {
          resortGroups.set(resortName, { type: "resort", resortName, hotels: [] });
        }

        resortGroups.get(resortName).hotels.push(hotel);
      });

      return resortGroups;
    },

    appendHotelItems(hotelsRootList, hotels, extraClassName = "") {
      hotels.forEach((hotel) => {
        hotelsRootList.append(service.createHotelListItem(hotel, extraClassName));
      });
    },

    appendGroupedHotels(hotelsRootList, group) {
      if (group.type === "resort" && group.hotels.length > 1) {
        const resortParent = {
          hotelName: group.resortName,
          _nodeId: service.buildResortParentNodeId(group.resortName),
        };

        hotelsRootList.append(service.createHotelListItem(resortParent, "mod--resortParent"));
        service.appendHotelItems(hotelsRootList, group.hotels, "mod--resortChild");
        return;
      }

      service.appendHotelItems(hotelsRootList, group.hotels);
    },

    renderHotelsListFromTree(hotelsRootList, treeData) {
      const allHotels = service.sortHotelsByRanking(service.flattenHotelsForRender(treeData));
      const groupedHotels = service.groupHotelsByResort(allHotels);

      hotelsRootList.innerHTML = "";

      groupedHotels.forEach((group) => {
        service.appendGroupedHotels(hotelsRootList, group);
      });

      host.applyHotelsVisibility(hotelsRootList.closest(host.props.cssSelector));
    },

    renderTreeFromSearchData(fastbooking) {
      const treeData = host.props.sourceTree;
      if (!Array.isArray(treeData) || !treeData.length) {
        return;
      }

      const defaultModal = fastbooking.querySelector(host.props.modals.destinations.default);
      const firstBlock = defaultModal?.querySelector(`.${host.props.modifiers.blocks.firstBlock}`);
      const secondBlock = defaultModal?.querySelector(`.${host.props.modifiers.blocks.secondBlock}`);
      const destinationRootList = service.getRootListFromBlock(firstBlock);
      const hotelsRootList = service.getRootListFromBlock(secondBlock);

      if (!destinationRootList || !hotelsRootList) {
        return;
      }

      const arrowTemplate = host?.props?.icons?.accordionArrow || firstBlock?.querySelector(".svg-icon.accordion-arrow")?.outerHTML;
      const hotelsLabel = String(fastbooking?.dataset?.hotelslabel || "hoteles").trim() || "hoteles";

      destinationRootList.innerHTML = "";
      treeData.forEach((rootDestination) => {
        destinationRootList.append(service.renderDestinationTreeNode(rootDestination, 1, arrowTemplate, hotelsLabel));
      });

      service.renderHotelsListFromTree(hotelsRootList, treeData);
      host.renderRecentSearches(fastbooking);
      host.updateRecentSearchesView(fastbooking);
      host.changeDestinationStructureOnMobile(fastbooking);
    },
  };

  return service;
};

export default createRenderService;
